import type { MutableRefObject } from "react";
import { fDate } from "src/utils/formatTime";
import {
  FORM_FIELD_KEYS,
  RECURRING_DATE_FIELDS,
} from "../constants/formFieldKeys";
import type { IFormConfig } from "../types";

const DATE_KEYS = new Set(["documentDate", "dueDate", "dueDeliveryDate"]);
const ARRAY_KEYS = new Set(["items", "taxes"]);

const ADDRESS_KEYS = new Set([
  "customerCompanyStreet",
  "customerCompanyStreetNumber",
  "customerCompanyLocality",
  "customerCompanyRegion",
  "customerCompanyZipCode",
  "customerCountry",
  "vendorCompanyStreet",
  "vendorCompanyStreetNumber",
  "vendorCompanyLocality",
  "vendorCompanyRegion",
  "vendorCompanyZipCode",
  "vendorCountry",
]);

interface IBuildPayloadArgs {
  values: Record<string, any>;
  fileName: string;
  pdfBlobRef?: MutableRefObject<Blob | null>;
  config: IFormConfig;
  isPRDraftBtnDisabled?: boolean;
}

export const buildPayload = ({
  values,
  fileName,
  pdfBlobRef,
  config,
  isPRDraftBtnDisabled,
}: IBuildPayloadArgs) => {
  const formData = new FormData();
  const P = isPRDraftBtnDisabled ? "updateDocumentDto" : "createDocumentDto";
  const omit = new Set(config.omitFromPayload ?? []);
  const {
    PDF_FILE,
    RECURRING_DOCUMENT,
    SPAIN_CORRECTION_CODE,
    UPLOADED_DOCUMENT,
  } = FORM_FIELD_KEYS;
  for (const [key, value] of Object.entries(values)) {
    if (omit.has(key)) continue;
    if (key.startsWith("_")) continue;

    if (DATE_KEYS.has(key)) {
      if (value) {
        formData.append(`${P}.${key}`, fDate(value, "yyyy-MM-dd"));
        continue;
      }
    }

    if (ARRAY_KEYS.has(key)) {
      (value as any[]).forEach((row, i) => {
        for (const [k, v] of Object.entries(row)) {
          if (v !== null && v !== undefined) {
            formData.append(`${P}.${key}[${i}].${k}`, v as string);
          }
        }
      });
      continue;
    }

    if (key === UPLOADED_DOCUMENT && value) {
      if (typeof value == "string") {
        formData.append(`${P}.ReferenceDocumentURL`, value);
      } else {
        formData.append(`${P}.${UPLOADED_DOCUMENT}`, value);
      }
      continue;
    }

    if (key === PDF_FILE && pdfBlobRef && pdfBlobRef.current) {
      formData.append(`${P}.${PDF_FILE}`, pdfBlobRef.current, "pdf");
      continue;
    }

    if (key === RECURRING_DOCUMENT) {
      const entries = Object.entries((value ?? {}) as Record<string, any>);
      const allFilled =
        entries.length > 0 &&
        entries.every(([_, v]) => v !== null && v !== undefined && v !== "");
      if (allFilled) {
        entries.forEach(([rKey, rVal]) => {
          const path = `${P}.${RECURRING_DOCUMENT}.${rKey}`;
          const out =
            rKey === RECURRING_DATE_FIELDS.START_DATE ||
            rKey === RECURRING_DATE_FIELDS.END_DATE
              ? fDate(rVal)
              : String(rVal);
          formData.append(path, out);
        });
      }
      continue;
    }

    if (ADDRESS_KEYS.has(key)) {
      if (value) {
        formData.append(`${P}.${key}`, String(value ?? ""));
        continue;
      }
    }

    if (key === SPAIN_CORRECTION_CODE && value) {
      formData.append(`${P}.SpainCorrectionCode`, value as string);
      continue;
    }

    if (value !== null && value !== undefined && value !== "") {
      formData.append(`${P}.${key}`, value as string);
    }
  }

  config.transformPayload?.(formData, values);

  return formData;
};
